import React, { useState, useEffect } from 'react';
import axios from 'axios';

function GHLConnectionStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reauthorizing, setReauthorizing] = useState(false);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      console.log('🔍 Checking GHL connection status...');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/ghl/status`);
      console.log('GHL Status response:', response.data);
      setStatus(response.data.data);
    } catch (error) {
      console.error('⚠️ Error checking GHL status:', error);
      console.error('Status check error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // If 401, user is not authenticated - don't show the component
      if (error.response?.status === 401) {
        setStatus(null);
      } else {
        // For other errors, assume GHL needs reconnection
        setStatus({ isConnected: false, needsReconnection: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReauthorize = async () => {
    setReauthorizing(true);
    try {
      console.log('🔄 Initiating GHL re-authorization...');
      console.log('API URL:', process.env.REACT_APP_API_URL);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/ghl/reauthorize`);

      console.log('✅ Re-auth response:', response.data);

      if (response.data.success && response.data.data.authUrl) {
        // Open auth URL in a popup window
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const authWindow = window.open(
          response.data.data.authUrl,
          'GHL Authorization',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no`
        );

        // Poll for window closure or success
        const checkWindowClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkWindowClosed);
            setReauthorizing(false);
            // Recheck status after auth window closes
            setTimeout(checkConnectionStatus, 1000);
          }
        }, 500);
      }
    } catch (error) {
      console.error('❌ Error initiating re-authorization:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      let errorMessage = 'Failed to initiate re-authorization. ';
      if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else {
        errorMessage += 'Please try again.';
      }

      alert(errorMessage);
      setReauthorizing(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Checking GHL connection...</div>
      </div>
    );
  }

  if (!status || !status.needsReconnection) {
    return null; // Don't show anything if connection is fine
  }

  return (
    <div style={styles.container}>
      <div style={styles.warningCard}>
        <div style={styles.iconContainer}>
          <span style={styles.warningIcon}>⚠️</span>
        </div>
        <div style={styles.content}>
          <h3 style={styles.title}>GoHighLevel Connection Required</h3>
          <p style={styles.description}>
            {!status.isConnected
              ? 'Your GoHighLevel account is not connected. Connect now to enable raffle creation and GHL integration.'
              : 'Your GoHighLevel token has expired. Reconnect to continue using GHL features like automatic product creation and webhook handling.'}
          </p>
          <button
            onClick={handleReauthorize}
            disabled={reauthorizing}
            style={styles.button}
          >
            {reauthorizing ? (
              <>
                <span style={styles.spinner}></span>
                Connecting...
              </>
            ) : (
              <>
                🔄 Reconnect GoHighLevel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    marginBottom: '20px'
  },
  loadingText: {
    color: '#6c757d',
    fontStyle: 'italic'
  },
  warningCard: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '20px',
    backgroundColor: '#fff3cd',
    border: '2px solid #ffc107',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  iconContainer: {
    marginRight: '15px',
    flexShrink: 0
  },
  warningIcon: {
    fontSize: '32px'
  },
  content: {
    flex: 1
  },
  title: {
    margin: '0 0 10px 0',
    color: '#856404',
    fontSize: '18px',
    fontWeight: 'bold'
  },
  description: {
    margin: '0 0 15px 0',
    color: '#856404',
    lineHeight: '1.6'
  },
  button: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    backgroundColor: '#28a745',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.8s linear infinite'
  }
};

// Add spinner animation
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`, styleSheet.cssRules.length);

export default GHLConnectionStatus;
